import { useState, useEffect, useCallback, RefObject, useRef } from 'react';

interface UseScrollToBottomOptions {
  threshold?: number; // Distance from bottom to consider "at bottom"
  debounceMs?: number; // Debounce scroll events
}

export const useScrollToBottom = (
  containerRef: RefObject<HTMLElement>,
  options: UseScrollToBottomOptions = {},
  messages: any[] = []
) => {
  const { threshold = 100, debounceMs = 50 } = options;
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [showButton, setShowButton] = useState(false);
  const [opacity, setOpacity] = useState(0);
  const prevUserMessagesLengthRef = useRef<number>(0);
  const scrollableElementRef = useRef<HTMLElement | Window | null>(null);

  // Find the actual scrollable element (container or its scrollable parent or window)
  const findScrollableElement = useCallback(() => {
    if (!containerRef.current) return null;

    let element: HTMLElement | null = containerRef.current;
    
    // Check if the container itself is scrollable
    const computedStyle = window.getComputedStyle(element);
    const isScrollable = 
      computedStyle.overflow === 'auto' ||
      computedStyle.overflow === 'scroll' ||
      computedStyle.overflowY === 'auto' ||
      computedStyle.overflowY === 'scroll';
    
    if (isScrollable && element.scrollHeight > element.clientHeight) {
      return element;
    }

    // Check parent elements
    element = element.parentElement;
    while (element && element !== document.body) {
      const style = window.getComputedStyle(element);
      const isParentScrollable = 
        style.overflow === 'auto' ||
        style.overflow === 'scroll' ||
        style.overflowY === 'auto' ||
        style.overflowY === 'scroll';
      
      if (isParentScrollable && element.scrollHeight > element.clientHeight) {
        return element;
      }
      element = element.parentElement;
    }

    // Fall back to window if no scrollable parent found
    return window;
  }, [containerRef]);

  const getScrollProperties = useCallback(() => {
    const scrollable = scrollableElementRef.current;
    
    if (!scrollable) return null;
    
    if (scrollable === window) {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;
      return { scrollTop, scrollHeight, clientHeight };
    } else {
      const element = scrollable as HTMLElement;
      return {
        scrollTop: element.scrollTop,
        scrollHeight: element.scrollHeight,
        clientHeight: element.clientHeight,
      };
    }
  }, []);

  const checkScrollPosition = useCallback(() => {
    const scrollable = scrollableElementRef.current || findScrollableElement();
    if (!scrollable) return;

    // Update ref before getting properties
    scrollableElementRef.current = scrollable;

    const props = getScrollProperties();
    if (!props) return;

    const { scrollTop, scrollHeight, clientHeight } = props;
    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
    const atBottom = distanceFromBottom <= threshold;

    setIsAtBottom(atBottom);
    setShowButton(!atBottom);

    // Calculate opacity based on distance from bottom
    // Opacity should be 0 when at bottom, gradually increase as user scrolls up
    const fadeThreshold = threshold * 3; // Start fading in when 3x threshold away
    const calculatedOpacity = atBottom 
      ? 0 
      : Math.min(1, Math.max(0, (distanceFromBottom - threshold) / (fadeThreshold - threshold)));
    
    setOpacity(calculatedOpacity);
  }, [findScrollableElement, getScrollProperties, threshold]);

  const scrollToBottom = useCallback(() => {
    const scrollable = scrollableElementRef.current || findScrollableElement();
    if (!scrollable) return;

    if (scrollable === window) {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: 'smooth'
      });
    } else {
      const element = scrollable as HTMLElement;
      element.scrollTo({
        top: element.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [findScrollableElement]);

  // Auto-scroll when user messages length changes (matching old useAutoScroll behavior)
  useEffect(() => {
    const userMessagesLength = messages.filter(message => message.role === 'user').length;
   
    
    // Only scroll when the number of user messages changes, not when message content changes
    if (userMessagesLength !== prevUserMessagesLengthRef.current && userMessagesLength > 0) {
      prevUserMessagesLengthRef.current = userMessagesLength;
      
      const scrollable = scrollableElementRef.current || findScrollableElement();
      if (!scrollable) return;

      if (scrollable === window) {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: 'smooth',
        });
      } else {
        const element = scrollable as HTMLElement;
        // Add custom scroll duration by temporarily setting CSS
        element.style.scrollBehavior = 'smooth';
        element.style.transition = 'scroll-top 0.4s ease-out';
        
        element.scrollTo({
          top: element.scrollHeight,
          behavior: 'smooth',
        });
        
        // Clean up transition after scroll completes
        setTimeout(() => {
          if (element) {
            element.style.transition = '';
          }
        }, 300);
      }
    }
  }, [messages, findScrollableElement]);

  useEffect(() => {
    const scrollable = findScrollableElement();
    if (!scrollable) return;

    scrollableElementRef.current = scrollable;

    let timeoutId: number;
    
    const debouncedCheckScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = window.setTimeout(checkScrollPosition, debounceMs);
    };

    // Initial check
    checkScrollPosition();

    // Add scroll listener
    if (scrollable === window) {
      window.addEventListener('scroll', debouncedCheckScroll, { passive: true });
    } else {
      const element = scrollable as HTMLElement;
      element.addEventListener('scroll', debouncedCheckScroll, { passive: true });
      
      // Also listen for resize events in case content changes
      const resizeObserver = new ResizeObserver(debouncedCheckScroll);
      resizeObserver.observe(element);

      return () => {
        clearTimeout(timeoutId);
        element.removeEventListener('scroll', debouncedCheckScroll);
        resizeObserver.disconnect();
      };
    }

    return () => {
      clearTimeout(timeoutId);
      if (scrollable === window) {
        window.removeEventListener('scroll', debouncedCheckScroll);
      }
    };
  }, [checkScrollPosition, debounceMs, messages, findScrollableElement]);

  return {
    isAtBottom,
    showButton,
    opacity,
    scrollToBottom
  };
};
